// Fase 15 — Super Admin System Console (punti 268-273): status, backup/restore,
// incidenti, release management con gate, Go-Live Checklist.
import { useCallback, useEffect, useState } from "react";
import { Activity, DatabaseBackup, History, Rocket, AlertOctagon, CheckCircle2, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const CHECK_STYLE = {
  PASSED: "bg-green-50 text-green-800 border-green-300",
  FAILED: "bg-red-50 text-red-800 border-red-300",
  WAIVED: "bg-amber-50 text-amber-800 border-amber-300",
  NOT_TESTED: "bg-slate-50 text-slate-600 border-slate-300",
};

export default function AdminSystem() {
  const { t } = useI18n();
  const [status, setStatus] = useState(null);
  const [golive, setGolive] = useState(null);
  const [releases, setReleases] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [busy, setBusy] = useState("");
  const [newVersion, setNewVersion] = useState("");
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [st, gl, rel, inc, pol] = await Promise.all([
        api.get("/admin/system/status"),
        api.get("/admin/system/go-live"),
        api.get("/admin/system/releases"),
        api.get("/admin/system/incidents"),
        api.get("/admin/system/security-policy"),
      ]);
      setStatus(st.data);
      setGolive(gl.data);
      setReleases(rel.data.releases || []);
      setIncidents(inc.data.incidents || []);
      setPolicy(pol.data);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runBackup = async () => {
    setBusy("backup");
    try {
      const job = (await api.post("/admin/system/backup")).data;
      toast.success("Backup completato", {
        description: `${job.collections} collezioni · ${job.documents} documenti · ${(job.size_bytes / 1048576).toFixed(1)} MB` });
      await load();
    } catch (e) {
      toast.error(t("common.error"), { description: formatApiError(e) });
    } finally { setBusy(""); }
  };

  const runDrill = async () => {
    setBusy("drill");
    try {
      const res = (await api.post("/admin/system/restore-drill")).data;
      if (res.status === "PASSED") {
        toast.success("Restore drill PASSED", {
          description: `${res.checks.length} collezioni validate nel database isolato` });
      } else {
        toast.error("Restore drill FAILED", { description: res.error || "Vedi dettagli" });
      }
      await load();
    } catch (e) {
      toast.error(t("common.error"), { description: formatApiError(e) });
    } finally { setBusy(""); }
  };

  const [evidenceDialog, setEvidenceDialog] = useState(null); // {item, status}
  const [evForm, setEvForm] = useState({});

  const setItemStatus = async (item, status) => {
    if (status === "PASSED" || status === "WAIVED") {
      setEvForm({
        evidence: item.evidence || "", test_ref: item.test_ref || "",
        environment: item.environment || "preview", app_version: item.app_version || "",
        waiver_reason: item.waiver_reason || "", waiver_expires_at: item.waiver_expires_at || "",
      });
      setEvidenceDialog({ item, status });
      return;
    }
    await submitItemStatus(item, status, {});
  };

  const submitItemStatus = async (item, status, extra) => {
    try {
      await api.put(`/admin/system/go-live/${item.id}`, {
        category: item.category, item: item.item, critical: item.critical,
        status, evidence: item.evidence, ...extra,
      });
      setEvidenceDialog(null);
      await load();
    } catch (e) {
      toast.error(t("common.error"), { description: formatApiError(e) });
    }
  };

  const createRelease = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/system/releases", { version: newVersion, status: "DRAFT" });
      toast.success(`Release ${newVersion} creata`);
      setNewVersion("");
      await load();
    } catch (err) {
      toast.error(t("common.error"), { description: formatApiError(err) });
    }
  };

  const advanceRelease = async (rel, status) => {
    try {
      await api.put(`/admin/system/releases/${rel.id}/status`, null, { params: { status } });
      toast.success(`Release ${rel.version} → ${status}`);
      await load();
    } catch (e) {
      const gate = e.response?.data?.detail;
      toast.error(t("common.error"), {
        description: typeof gate === "object" ? "Release gate non superato" : formatApiError(e) });
    }
  };

  const toggleMfa = async () => {
    const next = !policy.policy.mfa_enforced;
    try {
      await api.put("/admin/system/security-policy", { mfa_enforced: next });
      toast.success(next
        ? "MFA obbligatorio ATTIVO per Super Admin e Consulenti"
        : "MFA globale disattivato (il rollout pilot resta attivo)");
      await load();
    } catch (e) {
      toast.error(t("common.error"), { description: formatApiError(e) });
    }
  };

  if (error) return <p className="text-sm text-red-700" data-testid="admin-system-error">{error}</p>;
  if (!status || !golive) return <p className="text-sm text-slate-600" data-testid="admin-system-loading">{t("common.loading")}</p>;

  const ev = golive.evaluation;

  return (
    <div className="space-y-6" data-testid="admin-system-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <Activity className="w-7 h-7 text-green-800" aria-hidden="true" />
          Stato del sistema
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Versione, salute dei servizi, backup, incidenti, release e Go-Live Checklist. Nessuna credenziale è mai mostrata.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Versione", value: status.app_version, sub: status.environment, tid: "sys-version" },
          { label: "Database", value: status.database.ok ? "OK" : "DOWN",
            sub: status.database.latency_ms != null ? `${status.database.latency_ms} ms` : "—", tid: "sys-db" },
          { label: "Eva provider", value: status.eva_provider.configured ? (status.eva_provider.model || "configurato") : "NON CONFIGURATO",
            sub: status.eva_provider.enabled ? "attivo" : "disabilitato", tid: "sys-eva" },
          { label: "JOBTIMEHR", value: `${status.jobtimehr_connections} connessioni`, sub: "opzionale", tid: "sys-jt" },
          { label: "Update normativi", value: status.regulatory_pending, sub: "in valutazione", tid: "sys-reg" },
          { label: "Ultimo backup", value: status.last_backup ? status.last_backup.started_at.slice(0, 16).replace("T", " ") : "MAI",
            sub: status.last_backup ? `${(status.last_backup.size_bytes / 1048576).toFixed(1)} MB` : "", tid: "sys-backup" },
          { label: "Restore test", value: status.last_restore_test ? status.last_restore_test.status : "MAI",
            sub: status.last_restore_test ? status.last_restore_test.started_at.slice(0, 10) : "", tid: "sys-restore" },
          { label: "Incidenti aperti", value: status.open_incidents, sub: "operativi", tid: "sys-incidents" },
        ].map((c) => (
          <Card key={c.tid} className="border-slate-200" data-testid={c.tid}>
            <CardContent className="pt-4">
              <p className="text-[11px] text-slate-500">{c.label}</p>
              <p className="text-lg font-bold text-slate-900 truncate">{c.value}</p>
              <p className="text-[10px] text-slate-400">{c.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-slate-200" data-testid="backup-card">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
          <DatabaseBackup className="w-4 h-4" aria-hidden="true" /> Backup & Disaster Recovery
        </CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <Button onClick={runBackup} disabled={!!busy} className="bg-green-700 hover:bg-green-800" data-testid="backup-now-button">
            {busy === "backup" ? "Backup in corso…" : "Esegui backup ora"}
          </Button>
          <Button onClick={runDrill} disabled={!!busy} variant="outline" data-testid="restore-drill-button">
            <History className="w-4 h-4 mr-1" aria-hidden="true" />
            {busy === "drill" ? "Drill in corso…" : "Esegui restore drill (DB isolato)"}
          </Button>
          <p className="text-xs text-slate-500">
            Backup automatico giornaliero attivo (cron). Un backup è valido solo dopo un restore test superato.
          </p>
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="mfa-policy-card">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" aria-hidden="true" /> Sicurezza account — MFA TOTP
        </CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          {policy && (
            <>
              <Badge variant="outline" className={policy.policy.mfa_enforced
                ? "bg-green-50 text-green-800 border-green-300"
                : "bg-slate-50 text-slate-600 border-slate-300"}
                data-testid="mfa-enforced-badge">
                {policy.policy.mfa_enforced ? "ENFORCEMENT ATTIVO" : "ENFORCEMENT DISATTIVO"}
              </Badge>
              <p className="text-xs text-slate-600">
                Ruoli: {(policy.policy.mfa_required_for_roles || []).join(", ")} ·
                Utenti con MFA attivo: <strong>{policy.mfa_enrolled_users}</strong> ·
                Pilot: {(policy.policy.pilot_emails || []).length}
              </p>
              <Button onClick={toggleMfa} variant="outline" size="sm"
                data-testid="mfa-toggle-button">
                {policy.policy.mfa_enforced ? "Disattiva enforcement" : "Attiva enforcement globale"}
              </Button>
              <p className="text-[11px] text-slate-500 w-full">
                Login a due passaggi con app di autenticazione (TOTP, RFC 6238), recovery codes monouso,
                lockout dopo 5 tentativi, replay protection. Il rollout graduale per singolo utente
                (pilot) è gestito lato policy.
              </p>
            </>
          )}
        </CardContent>
      </Card>

      <Card className={`border-2 ${ev.verdict === "READY FOR GO-LIVE" ? "border-green-300" : ev.verdict === "CONDITIONALLY READY" ? "border-amber-300" : "border-red-300"}`} data-testid="golive-verdict-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Rocket className="w-4 h-4" aria-hidden="true" /> Go-Live Gate
            <Badge variant="outline" className={ev.verdict === "READY FOR GO-LIVE"
              ? "bg-green-50 text-green-800 border-green-300"
              : ev.verdict === "CONDITIONALLY READY"
                ? "bg-amber-50 text-amber-800 border-amber-300"
                : "bg-red-50 text-red-800 border-red-300"}
              data-testid="golive-verdict">
              {ev.verdict}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {ev.blockers.map((b, i) => (
            <p key={i} className="text-xs text-red-800" data-testid={`golive-blocker-${i}`}>
              <AlertOctagon className="w-3 h-3 inline mr-1" aria-hidden="true" />
              {b.type}: {(b.items || []).slice(0, 3).join("; ")}{b.count ? ` (${b.count})` : ""}
            </p>
          ))}
          {ev.warnings.map((w, i) => (
            <p key={i} className="text-xs text-amber-800" data-testid={`golive-warning-${i}`}>
              {w.type}: {(w.items || []).length} item
            </p>
          ))}
          <p className="text-xs text-slate-500">
            Backup: {ev.backup_ok ? "OK" : "mancante"} · Restore test: {ev.restore_tested ? "superato" : "mancante"}
          </p>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {golive.items.map((item) => (
              <div key={item.id} className="flex flex-wrap items-center gap-2 text-xs border-b border-slate-100 pb-1.5"
                data-testid={`golive-item-${item.id}`}>
                <Badge variant="outline" className="text-[9px]">{item.category}</Badge>
                <span className="flex-1 min-w-0 text-slate-700">
                  <span className="block truncate">{item.item}</span>
                  {item.test_ref && (
                    <span className="block text-[10px] text-slate-400 truncate" data-testid={`golive-evidence-${item.id}`}>
                      {item.test_ref} · {item.environment || "—"} · {(item.tested_at || "").slice(0, 10)}
                    </span>
                  )}
                </span>
                {item.critical && <Badge variant="outline" className="bg-red-50 text-red-800 border-red-300 text-[9px]">CRITICAL</Badge>}
                <Select value={item.status} onValueChange={(v) => setItemStatus(item, v)}>
                  <SelectTrigger className="h-7 w-32 text-[11px]" data-testid={`golive-status-${item.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["NOT_TESTED", "PASSED", "FAILED", "WAIVED"].map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-slate-200" data-testid="releases-card">
          <CardHeader className="pb-2"><CardTitle className="text-base">Release management</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <form onSubmit={createRelease} className="flex gap-2">
              <Input value={newVersion} onChange={(e) => setNewVersion(e.target.value)}
                placeholder="es. 1.15.0" className="h-9 w-40" data-testid="release-version-input" />
              <Button type="submit" size="sm" className="bg-green-700 hover:bg-green-800"
                disabled={!newVersion} data-testid="release-create-button">Crea release</Button>
            </form>
            {releases.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-2 text-xs border-b border-slate-100 pb-2"
                data-testid={`release-${r.id}`}>
                <strong className="text-slate-900">v{r.version}</strong>
                <span className="text-slate-500">{r.environment}</span>
                <Badge variant="outline" className="bg-slate-50">{r.status}</Badge>
                <span className="text-slate-400">help {r.help_version} · prompt Eva v{r.eva_prompt_version || "?"}</span>
                {r.status === "TESTING" && (
                  <Button size="sm" variant="outline" className="h-7 text-xs ml-auto"
                    onClick={() => advanceRelease(r, "RELEASE_CANDIDATE")}
                    data-testid={`release-rc-${r.id}`}>→ Release candidate</Button>
                )}
                {r.status === "RELEASE_CANDIDATE" && (
                  <Button size="sm" className="h-7 text-xs ml-auto bg-green-700 hover:bg-green-800"
                    onClick={() => advanceRelease(r, "APPROVED")}
                    data-testid={`release-approve-${r.id}`}>
                    <CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" /> Approva (gate)
                  </Button>
                )}
              </div>
            ))}
            {releases.length === 0 && <p className="text-xs text-slate-500">Nessuna release registrata.</p>}
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="incidents-card">
          <CardHeader className="pb-2"><CardTitle className="text-base">Incidenti operativi</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {incidents.length === 0 && (
              <p className="text-xs text-slate-500">Nessun incidente registrato.</p>
            )}
            {incidents.slice(0, 10).map((i) => (
              <div key={i.id} className="flex items-center gap-2 text-xs border-b border-slate-100 pb-1.5"
                data-testid={`incident-${i.id}`}>
                <Badge variant="outline" className={i.severity === "SEV1"
                  ? "bg-red-50 text-red-800 border-red-300" : "bg-slate-50"}>{i.severity}</Badge>
                <span className="flex-1 truncate text-slate-700">{i.title}</span>
                <span className="text-slate-500">{i.status}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!evidenceDialog} onOpenChange={() => setEvidenceDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="golive-evidence-dialog">
          <DialogHeader>
            <DialogTitle>
              {evidenceDialog?.status === "PASSED" ? "Registra evidenza del test" : "Registra deroga (waiver)"}
            </DialogTitle>
            <DialogDescription>
              {evidenceDialog?.status === "PASSED"
                ? "Un controllo può essere PASSED solo con test eseguito ed evidenza consultabile."
                : "Una deroga su item critico richiede motivazione e data di scadenza."}
            </DialogDescription>
          </DialogHeader>
          {evidenceDialog && (
            <div className="space-y-3">
              <p className="text-xs font-medium text-slate-700">{evidenceDialog.item.item}</p>
              {evidenceDialog.status === "PASSED" && (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Riferimento test *</Label>
                    <Input value={evForm.test_ref || ""} placeholder="es. tests/test_phase15_security.py::TestXss"
                      onChange={(e) => setEvForm({ ...evForm, test_ref: e.target.value })}
                      data-testid="golive-test-ref-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Evidenza consultabile *</Label>
                    <Input value={evForm.evidence || ""} placeholder="es. /app/test_reports/iteration_31.json — 7/7 PASSED"
                      onChange={(e) => setEvForm({ ...evForm, evidence: e.target.value })}
                      data-testid="golive-evidence-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Ambiente</Label>
                      <Input value={evForm.environment || ""}
                        onChange={(e) => setEvForm({ ...evForm, environment: e.target.value })}
                        data-testid="golive-environment-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Versione app</Label>
                      <Input value={evForm.app_version || ""} placeholder="es. commit b572088"
                        onChange={(e) => setEvForm({ ...evForm, app_version: e.target.value })}
                        data-testid="golive-version-input" />
                    </div>
                  </div>
                </>
              )}
              {evidenceDialog.status === "WAIVED" && (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Motivazione della deroga *</Label>
                    <Input value={evForm.waiver_reason || ""}
                      onChange={(e) => setEvForm({ ...evForm, waiver_reason: e.target.value })}
                      data-testid="golive-waiver-reason-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Scadenza deroga *</Label>
                    <Input type="date" value={evForm.waiver_expires_at || ""}
                      onChange={(e) => setEvForm({ ...evForm, waiver_expires_at: e.target.value })}
                      data-testid="golive-waiver-expiry-input" />
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEvidenceDialog(null)}
              data-testid="golive-evidence-cancel">Annulla</Button>
            <Button className="bg-green-700 hover:bg-green-800"
              onClick={() => submitItemStatus(evidenceDialog.item, evidenceDialog.status, evForm)}
              data-testid="golive-evidence-confirm">
              Conferma {evidenceDialog?.status}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
